import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { FormData } from '../../pages/ApplicationV3/validation/formSchema';
import {
  AddressCountryEnum,
  ApplicationModel as V3ApplicationModel,
  ApplicationSteps,
  ApplicationTypeEnum,
  ConfirmEmploymentEnum,
  EmploymentPeriodView,
  NonQualifyingPeriodView,
  MonthData,
} from '../../types/ApplicationV3/application';
import {
  ApplicationFullTexts,
  ApplicationStartTexts,
  FormContent,
} from '../../types/ApplicationV3/applicationTexts';
import { BisnodePerson } from '../../types/Bisnode/BisnodePerson';
import { ResultData } from '../../pages/ApplicationV3/hooks/useResultData';
import { transformForaResult } from '../../Functions/Fora';
import { buildMonthData } from '../../Functions/Application';
import { ApplicationFile } from '../../types/Application/application';
import { PremiumPaymentThresholdContainer } from '../../types/Config';

const uniqById = <T extends { id: string }>(items: T[]): T[] => {
  const seen = new Set<string>();
  const result: T[] = [];
  for (const item of items) {
    if (seen.has(item.id)) continue;
    seen.add(item.id);
    result.push(item);
  }
  return result;
};

const normalizeIsoDate = (value: string | undefined): string => {
  return (value ?? '').slice(0, 10);
};

const normalizeEmploymentPeriodDates = (
  employment: EmploymentPeriodView
): EmploymentPeriodView => {
  return {
    ...employment,
    startDate: normalizeIsoDate(employment.startDate),
    endDate: normalizeIsoDate(employment.endDate),
    noticeDate: normalizeIsoDate(employment.noticeDate),
  };
};

const normalizeNonQualifyingPeriodDates = (
  period: NonQualifyingPeriodView
): NonQualifyingPeriodView => {
  return {
    ...period,
    startDate: normalizeIsoDate(period.startDate),
    endDate: normalizeIsoDate(period.endDate),
  };
};

const mergeWorkedMonths = (
  applicationMonths: EmploymentPeriodView['workedMonths'] | undefined,
  foraMonths: EmploymentPeriodView['workedMonths'] | undefined
): EmploymentPeriodView['workedMonths'] => {
  const appMonths = applicationMonths ?? [];
  const fora = foraMonths ?? [];

  const keyOf = (m: { year: number; month: number }) => `${m.year}-${m.month}`;
  const foraByKey = new Map(fora.map((m) => [keyOf(m), m]));
  const appByKey = new Map(appMonths.map((m) => [keyOf(m), m]));

  const allKeys = new Set<string>([...appByKey.keys(), ...foraByKey.keys()]);
  const merged: EmploymentPeriodView['workedMonths'] = [];

  for (const key of allKeys) {
    const appMonth = appByKey.get(key);
    const foraMonth = foraByKey.get(key);
    const base = (appMonth ?? foraMonth)!;

    merged.push({
      ...base,
      // Keep user attachments from the saved application.
      attachments: appMonth?.attachments ?? foraMonth?.attachments,
      // Use fresh payroll flags when present.
      hasPayroll: foraMonth?.hasPayroll ?? appMonth?.hasPayroll ?? false,
      hasAttachmentFromPreviousApplication:
        appMonth?.hasAttachmentFromPreviousApplication ?? false,
      meetsSalaryRequirement:
        foraMonth?.meetsSalaryRequirement ?? appMonth?.meetsSalaryRequirement,
      meetsSalaryRequirementWithAnotherEmployment:
        foraMonth?.meetsSalaryRequirementWithAnotherEmployment ??
        appMonth?.meetsSalaryRequirementWithAnotherEmployment,
    });
  }

  return merged.sort((a, b) => a.year - b.year || a.month - b.month);
};

const mergeEmploymentPeriods = (
  applicationEmployments: EmploymentPeriodView[],
  foraEmployments: EmploymentPeriodView[]
): EmploymentPeriodView[] => {
  if (!foraEmployments?.length) return applicationEmployments;
  if (!applicationEmployments?.length) return foraEmployments;

  const usedForaIndexes = new Set<number>();

  const findMatchingForaEmployment = (
    appEmployment: EmploymentPeriodView
  ): { emp: EmploymentPeriodView; index: number } | null => {
    const appOrg = appEmployment.organizationNumber;
    if (!appOrg) return null;

    const appStart = normalizeIsoDate(appEmployment.startDate);
    const appEnd = normalizeIsoDate(appEmployment.endDate);

    const candidates: Array<{ emp: EmploymentPeriodView; index: number }> = [];
    foraEmployments.forEach((emp, index) => {
      if (usedForaIndexes.has(index)) return;
      if (emp.organizationNumber !== appOrg) return;
      candidates.push({ emp, index });
    });

    if (!candidates.length) return null;

    // 1) Exact start/end date match
    const matchByDates = candidates.find((c) => {
      const start = normalizeIsoDate(c.emp.startDate);
      const end = normalizeIsoDate(c.emp.endDate);
      return start === appStart && end === appEnd;
    });

    if (matchByDates) return matchByDates;

    // 2) Partial date match (start OR end)
    const matchByStartOrEnd = candidates.find((c) => {
      const start = normalizeIsoDate(c.emp.startDate);
      const end = normalizeIsoDate(c.emp.endDate);
      return (appStart && start === appStart) || (appEnd && end === appEnd);
    });
    if (matchByStartOrEnd) return matchByStartOrEnd;

    // 3) Match by orgNumber only if app dates are missing for a employment that is marked as CSN completion.
    if (!appStart && !appEnd && appEmployment.isCsnCompletionEmployment) {
      return candidates[0];
    }
    return null;
  };

  const merged = applicationEmployments.map((appEmployment) => {
    const match = findMatchingForaEmployment(appEmployment);
    if (!match) return appEmployment;

    usedForaIndexes.add(match.index);
    const foraEmployment = match.emp;

    // Preserve the saved application as the source of truth,
    // but enrich it with the latest Fora payroll flags.
    const result: EmploymentPeriodView = {
      ...foraEmployment,
      ...appEmployment,
      id: appEmployment.id,
      agreement: appEmployment.agreement ?? foraEmployment.agreement,
      retrievedFromFora:
        appEmployment.retrievedFromFora || foraEmployment.retrievedFromFora,
      workedMonths: mergeWorkedMonths(
        appEmployment.workedMonths,
        foraEmployment.workedMonths
      ),
      startDate:
        appEmployment.startDate !== ''
          ? appEmployment.startDate
          : foraEmployment.startDate,
      endDate:
        appEmployment.endDate !== ''
          ? appEmployment.endDate
          : foraEmployment.endDate,
    };

    return result;
  });

  // Add any additional employments that exist in Fora but not in the saved application.
  foraEmployments.forEach((emp, index) => {
    if (!usedForaIndexes.has(index)) merged.push(emp);
  });

  return merged;
};

export interface Baseline {}

export interface Prefill {}

export interface EmployerAttachmentPayload {
  employerId: string;
  month: number;
  year: number;
  attachment: ApplicationFile;
}

export interface ApplicationState {
  incidentId?: string;
  premiumPaymentThreshold?: PremiumPaymentThresholdContainer;
  activeStep: ApplicationSteps;
  applicationType: ApplicationTypeEnum | null;
  isCompletingExistingApplication: boolean;
  startTexts: ApplicationStartTexts | null;
  fullTexts: ApplicationFullTexts | null;
  formTexts: FormContent | null;
  sessionId: string;
  isForaAssured: boolean | undefined;
  retrievedFromBisnode: boolean;
  protectedIdentity?: boolean;
  gedi?: string;
  foraEmployments: EmploymentPeriodView[];
  employments: EmploymentPeriodView[];
  nonQualifyingPeriods: NonQualifyingPeriodView[];
  baseline: Partial<FormData> | null;
  prefill: Partial<FormData> | null;
  formData: Partial<FormData> | null;
  monthData: MonthData[];
  signedApplicationDocument?: ApplicationFile | null;
}

const initialState: ApplicationState = {
  premiumPaymentThreshold: undefined,
  activeStep: ApplicationSteps.Start,
  applicationType: null,
  isCompletingExistingApplication: false,
  startTexts: null,
  fullTexts: null,
  formTexts: null,
  sessionId: '',
  isForaAssured: undefined,
  retrievedFromBisnode: false,
  protectedIdentity: undefined,
  foraEmployments: [],
  employments: [],
  nonQualifyingPeriods: [],
  baseline: null,
  prefill: null,
  formData: {
    addressCountry: AddressCountryEnum.Sweden,
  },
  monthData: [],
  signedApplicationDocument: null,
};

const applicationSlice = createSlice({
  name: 'application',
  initialState,
  reducers: {
    resetState: () => initialState,
    setPremiumPaymentThreshold: (
      state,
      action: PayloadAction<PremiumPaymentThresholdContainer>
    ) => {
      state.premiumPaymentThreshold = action.payload;
    },
    setActiveStep: (state, action: PayloadAction<ApplicationSteps>) => {
      state.activeStep = action.payload;
    },
    setSessionId: (state, action: PayloadAction<string>) => {
      state.sessionId = action.payload;
    },
    setIsCompletingExistingApplication: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.isCompletingExistingApplication = action.payload;
    },
    setIsForaAssured: (state, action: PayloadAction<boolean | undefined>) => {
      state.isForaAssured = action.payload;
    },
    setApplicationType: (
      state,
      action: PayloadAction<ApplicationTypeEnum | null>
    ) => {
      state.applicationType = action.payload;
      // Completion mode must persist even if applicationType is later overwritten
      // from a retrieved incident. Therefore:
      // - Always set to true when user selects CompletionForApplication
      // - Only clear it when user is on the Start step and selects another type
      if (action.payload === ApplicationTypeEnum.CompletionForApplication) {
        state.isCompletingExistingApplication = true;
      } else if (state.activeStep === ApplicationSteps.Start) {
        state.isCompletingExistingApplication = false;
      }
    },
    setStartTexts: (state, action: PayloadAction<ApplicationStartTexts>) => {
      state.startTexts = action.payload;
    },
    setFullTexts: (state, action: PayloadAction<ApplicationFullTexts>) => {
      state.fullTexts = action.payload;
    },
    setFormTexts: (state, action: PayloadAction<FormContent>) => {
      state.formTexts = action.payload;
    },
    setResultData: (state, action: PayloadAction<ResultData>) => {
      const { person, payroll } = action.payload;
      if (!person || !payroll || !state.premiumPaymentThreshold) return;
      const transformedData = transformForaResult(
        person,
        payroll,
        state.premiumPaymentThreshold
      );

      state.foraEmployments = transformedData;
      state.formData = {
        ...state.formData,
        socialSecurityNumber: person.identityNumber,
      };

      // In completion flow we may already have employments from a retrieved incident.
      // Keep those as the base, but merge in Fora payroll flags.
      if (
        state.isCompletingExistingApplication &&
        state.employments.length > 0
      ) {
        state.employments = mergeEmploymentPeriods(
          state.employments,
          transformedData
        ).map(normalizeEmploymentPeriodDates);
      } else {
        state.employments = transformedData;
      }
      state.monthData = buildMonthData(
        state.employments,
        state.nonQualifyingPeriods,
        undefined,
        state.premiumPaymentThreshold
      );
    },
    setPersonData: (state, action: PayloadAction<BisnodePerson>) => {
      const {
        addressList,
        preferredFirstName,
        familyName,
        phoneList,
        protectedIdentity,
        gedi,
      } = action.payload;
      const address = addressList?.[0];
      const { streetName, streetNumber, postalCode, city } = address;
      const street = `${streetName} ${streetNumber}`;
      const phone = phoneList?.find((p) => p.type === 'Mobile');

      state.protectedIdentity = protectedIdentity;
      state.retrievedFromBisnode = true;
      state.gedi = gedi;

      state.formData = {
        ...state.formData,
        firstname: preferredFirstName,
        lastname: familyName,
        address: street,
        zipCode: postalCode,
        city,
        phone: phone?.number,
      };
    },
    setMonthData: (state) => {
      const monthData = buildMonthData(
        state.employments,
        state.nonQualifyingPeriods,
        undefined,
        state.premiumPaymentThreshold
      );
      state.monthData = monthData;
    },
    addEmployment: (state, action: PayloadAction<EmploymentPeriodView>) => {
      state.employments.push(action.payload);
      state.monthData = buildMonthData(
        state.employments,
        state.nonQualifyingPeriods,
        undefined,
        state.premiumPaymentThreshold
      );
    },
    removeEmployment: (state, action: PayloadAction<string>) => {
      state.employments = state.employments.filter(
        (employment) => employment.id !== action.payload
      );
      state.monthData = buildMonthData(
        state.employments,
        state.nonQualifyingPeriods,
        undefined,
        state.premiumPaymentThreshold
      );
    },
    updateEmployment: (state, action: PayloadAction<EmploymentPeriodView>) => {
      const index = state.employments.findIndex(
        (employment) => employment.id === action.payload.id
      );
      if (index !== -1) {
        state.employments[index] = action.payload;
        state.monthData = buildMonthData(
          state.employments,
          state.nonQualifyingPeriods,
          undefined,
          state.premiumPaymentThreshold
        );
      }
    },
    updateEmployerAttachment: (
      state,
      action: PayloadAction<EmployerAttachmentPayload>
    ) => {
      const { employerId, month, year, attachment } = action.payload;
      // Find the month data
      const monthDataIndex = state.monthData.findIndex(
        (m) => m.month === month && m.year === year
      );

      if (monthDataIndex !== -1) {
        // Find the employer within that month
        const employerIndex = state.monthData[
          monthDataIndex
        ].employers.findIndex((emp) => emp.id === employerId);

        if (employerIndex !== -1) {
          // Update the employer's attachment
          const employerAttachments =
            state.monthData[monthDataIndex].employers[employerIndex]
              .attachments || [];
          employerAttachments.push(attachment);
          state.monthData[monthDataIndex].employers[employerIndex].attachments =
            employerAttachments;
          state.monthData[monthDataIndex].employers[
            employerIndex
          ].hasAttachment = true;
        }
      }

      // Also update in the employments array if needed
      const employment = state.employments.find((emp) => emp.id === employerId);
      if (employment) {
        employment.workedMonths = employment.workedMonths.map((wm) => {
          if (wm.year === year && wm.month === month) {
            return {
              ...wm,
              hasAttachment: true,
              attachments: [...(wm.attachments || []), attachment],
            };
          }

          return wm;
        });
      }
    },
    removeEmployerAttachment: (
      state,
      action: PayloadAction<{
        employerId: string;
        attachmentId: string;
        month: number;
        year: number;
      }>
    ) => {
      const { employerId, month, year, attachmentId } = action.payload;

      const monthDataIndex = state.monthData.findIndex(
        (m) => m.month === month && m.year === year
      );

      if (monthDataIndex !== -1) {
        const employerIndex = state.monthData[
          monthDataIndex
        ].employers.findIndex((emp) => emp.id === employerId);

        if (employerIndex !== -1) {
          state.monthData[monthDataIndex].employers[employerIndex].attachments =
            state.monthData[monthDataIndex].employers[
              employerIndex
            ].attachments?.filter((att) => att.id !== attachmentId);
          state.monthData[monthDataIndex].employers[
            employerIndex
          ].hasAttachment =
            state.monthData[monthDataIndex].employers[employerIndex].attachments
              ?.length! > 0;
        }
      }
      const employment = state.employments.find((emp) => emp.id === employerId);
      if (employment) {
        employment.workedMonths = employment.workedMonths.map((wm) => {
          if (wm.year === year && wm.month === month) {
            return {
              ...wm,
              hasAttachment: wm.attachments
                ? wm.attachments.filter((att) => att.id !== attachmentId)
                    .length > 0
                : false,
              attachments: wm.attachments?.filter(
                (att) => att.id !== attachmentId
              ),
            };
          }

          return wm;
        });
      }
    },
    addNonQualifyingPeriod: (
      state,
      action: PayloadAction<NonQualifyingPeriodView>
    ) => {
      state.nonQualifyingPeriods = state.nonQualifyingPeriods || [];
      state.nonQualifyingPeriods.push(action.payload);
      state.monthData = buildMonthData(
        state.employments,
        state.nonQualifyingPeriods,
        undefined,
        state.premiumPaymentThreshold
      );
    },
    removeNonQualifyingPeriod: (state, action: PayloadAction<string>) => {
      state.nonQualifyingPeriods = state.nonQualifyingPeriods?.filter(
        (period) => period.id !== action.payload
      );
      state.monthData = buildMonthData(
        state.employments,
        state.nonQualifyingPeriods,
        undefined,
        state.premiumPaymentThreshold
      );
    },
    updateNonQualifyingPeriod: (
      state,
      action: PayloadAction<NonQualifyingPeriodView>
    ) => {
      state.nonQualifyingPeriods = state.nonQualifyingPeriods?.map((period) =>
        period.id === action.payload.id ? action.payload : period
      );
      state.monthData = buildMonthData(
        state.employments,
        state.nonQualifyingPeriods,
        undefined,
        state.premiumPaymentThreshold
      );
    },
    createOrUpdateLatestEmployment: (
      state,
      action: PayloadAction<EmploymentPeriodView>
    ) => {
      let index = state.employments.findIndex(
        (employment) => employment.id === action.payload.id
      );

      // Keep the latest employment stable even if the payload id changes/missing.
      // This prevents duplicate "latest" employments and losing attachments on submit.
      if (index === -1 && action.payload.isLatestEmployment) {
        const latestIndex = state.employments.findIndex(
          (e) =>
            e.isLatestEmployment &&
            e.organizationNumber === action.payload.organizationNumber
        );
        if (latestIndex !== -1) {
          index = latestIndex;
        }
      }

      // Ensure only one employment is marked as latest
      if (action.payload.isLatestEmployment) {
        state.employments.forEach((emp, i) => {
          if (i !== index) {
            emp.isLatestEmployment = false;
          }
        });
      }

      if (index !== -1) {
        const existingId = state.employments[index].id;
        state.employments[index] = action.payload.isLatestEmployment
          ? { ...action.payload, id: existingId }
          : action.payload;
      } else {
        state.employments.push(action.payload);
      }
      state.monthData = buildMonthData(
        state.employments,
        state.nonQualifyingPeriods,
        undefined,
        state.premiumPaymentThreshold
      );
    },
    addSicknessAttachmentLatestEmployment: (
      state,
      action: PayloadAction<ApplicationFile>
    ) => {
      const latestEmployment = state.employments.find(
        (e) => e.isLatestEmployment
      );
      if (latestEmployment) {
        latestEmployment.sicknessAttachments = [
          ...(latestEmployment.sicknessAttachments || []),
          action.payload,
        ];
      }
    },
    addEmploymentCertificateLatestEmployment: (
      state,
      action: PayloadAction<ApplicationFile>
    ) => {
      const latestEmployment = state.employments.find(
        (e) => e.isLatestEmployment
      );
      if (latestEmployment) {
        latestEmployment.employmentCertificates = [
          ...(latestEmployment.employmentCertificates || []),
          action.payload,
        ];
      }
    },
    addWorkedTimeEmploymentCertificateLatestEmployment: (
      state,
      action: PayloadAction<ApplicationFile>
    ) => {
      const latestEmployment = state.employments.find(
        (e) => e.isLatestEmployment
      );
      if (!latestEmployment) return;

      latestEmployment.workedTimeEmploymentCertificates = uniqById([
        ...(latestEmployment.workedTimeEmploymentCertificates || []),
        action.payload,
      ]);

      state.monthData = buildMonthData(
        state.employments,
        state.nonQualifyingPeriods,
        undefined,
        state.premiumPaymentThreshold
      );
    },
    addLatestPayslipAttachmentLatestEmployment: (
      state,
      action: PayloadAction<ApplicationFile>
    ) => {
      const latestEmployment = state.employments.find(
        (e) => e.isLatestEmployment
      );
      if (latestEmployment) {
        latestEmployment.latestPayslipAttachments = uniqById([
          ...(latestEmployment.latestPayslipAttachments || []),
          action.payload,
        ]);
        latestEmployment.hasPayslipToProveOngoingEmployment =
          state.applicationType === ApplicationTypeEnum.OngoingEmployment &&
          (latestEmployment.latestPayslipAttachments?.length ?? 0) > 0;
      }
    },
    removeSicknessAttachmentLatestEmployment: (
      state,
      action: PayloadAction<string>
    ) => {
      const latestEmployment = state.employments.find(
        (e) => e.isLatestEmployment
      );
      if (latestEmployment) {
        latestEmployment.sicknessAttachments =
          latestEmployment.sicknessAttachments?.filter(
            (att) => att.id !== action.payload
          );
      }
    },
    removeEmploymentCertificateLatestEmployment: (
      state,
      action: PayloadAction<string>
    ) => {
      const latestEmployment = state.employments.find(
        (e) => e.isLatestEmployment
      );
      if (latestEmployment) {
        latestEmployment.employmentCertificates =
          latestEmployment.employmentCertificates?.filter(
            (att) => att.id !== action.payload
          );
      }
    },
    removeWorkedTimeEmploymentCertificateLatestEmployment: (
      state,
      action: PayloadAction<string>
    ) => {
      const latestEmployment = state.employments.find(
        (e) => e.isLatestEmployment
      );
      if (!latestEmployment) return;

      latestEmployment.workedTimeEmploymentCertificates =
        latestEmployment.workedTimeEmploymentCertificates?.filter(
          (att) => att.id !== action.payload
        );

      state.monthData = buildMonthData(
        state.employments,
        state.nonQualifyingPeriods,
        undefined,
        state.premiumPaymentThreshold
      );
    },
    removeLatestPayslipAttachmentLatestEmployment: (
      state,
      action: PayloadAction<string>
    ) => {
      const latestEmployment = state.employments.find(
        (e) => e.isLatestEmployment
      );
      if (latestEmployment) {
        latestEmployment.latestPayslipAttachments =
          latestEmployment.latestPayslipAttachments?.filter(
            (att) => att.id !== action.payload
          );
        latestEmployment.hasPayslipToProveOngoingEmployment =
          state.applicationType === ApplicationTypeEnum.OngoingEmployment &&
          (latestEmployment.latestPayslipAttachments?.length ?? 0) > 0;
      }
    },
    removeLatestEmployment: (state) => {
      state.employments = state.employments.filter(
        (employment) => !employment.isLatestEmployment
      );
    },
    setLatestEmployment: (state, action: PayloadAction<string>) => {
      const previousLatestIndex = state.employments.findIndex(
        (emp) => emp.isLatestEmployment
      );
      const nextLatestIndex = state.employments.findIndex(
        (emp) => emp.id === action.payload
      );

      if (
        previousLatestIndex !== -1 &&
        nextLatestIndex !== -1 &&
        previousLatestIndex !== nextLatestIndex
      ) {
        const previousLatest = state.employments[previousLatestIndex];

        // In identified (Fora) flow, switching latest employment should discard
        // latest-only edits on the previously selected employment.
        if (
          !state.isCompletingExistingApplication &&
          previousLatest.retrievedFromFora === true
        ) {
          const snapshot =
            state.foraEmployments.find((emp) => emp.id === previousLatest.id) ??
            state.foraEmployments.find(
              (emp) =>
                emp.organizationNumber === previousLatest.organizationNumber &&
                normalizeIsoDate(emp.startDate) ===
                  normalizeIsoDate(previousLatest.startDate) &&
                normalizeIsoDate(emp.endDate) ===
                  normalizeIsoDate(previousLatest.endDate)
            );

          if (snapshot) {
            state.employments[previousLatestIndex] = {
              ...snapshot,
              isLatestEmployment: false,
            };
          } else {
            state.employments[previousLatestIndex] = {
              ...previousLatest,
              isLatestEmployment: false,
            };
          }
        } else {
          state.employments[previousLatestIndex] = {
            ...previousLatest,
            isLatestEmployment: false,
          };
        }
      } else {
        state.employments.forEach((emp) => {
          emp.isLatestEmployment = false;
        });
      }

      if (nextLatestIndex !== -1) {
        state.employments[nextLatestIndex] = {
          ...state.employments[nextLatestIndex],
          isLatestEmployment: true,
        };
      }

      state.monthData = buildMonthData(
        state.employments,
        state.nonQualifyingPeriods,
        undefined,
        state.premiumPaymentThreshold
      );
    },
    setFormData: (state, action: PayloadAction<Partial<FormData>>) => {
      state.formData = {
        ...state.formData,
        ...action.payload,
      };
    },
    setSignedApplicationDocument: (
      state,
      action: PayloadAction<ApplicationFile | null>
    ) => {
      state.signedApplicationDocument = action.payload;
    },

    prefillFromLatestIncident: (
      state,
      action: PayloadAction<V3ApplicationModel>
    ) => {
      const foraEmploymentsBeforePrefill = state.foraEmployments;

      const latestIncident = action.payload;
      const contact = latestIncident.applicantContactDetails;

      const incidentEmploymentsRaw = latestIncident.employmentPeriods ?? [];
      const incidentEmployments = incidentEmploymentsRaw.map(
        normalizeEmploymentPeriodDates
      );
      const latestEmployment =
        incidentEmployments.find((e) => e.isLatestEmployment) ??
        incidentEmployments[0];

      const mergedEmployments = mergeEmploymentPeriods(
        incidentEmployments,
        foraEmploymentsBeforePrefill
      ).map(normalizeEmploymentPeriodDates);

      state.incidentId = latestIncident.incidentId;
      state.applicationType =
        latestIncident.applicationType as ApplicationTypeEnum;
      state.employments = mergedEmployments;
      state.nonQualifyingPeriods = (
        latestIncident.nonQualifyingPeriods ?? []
      ).map(normalizeNonQualifyingPeriodDates);
      state.signedApplicationDocument =
        latestIncident.signedApplicationDocument ?? null;
      state.formData = {
        ...state.formData,
        socialSecurityNumber:
          contact?.socialSecurityNumber ?? state.formData?.socialSecurityNumber,
        firstname: contact?.firstname ?? state.formData?.firstname,
        lastname: contact?.lastname ?? state.formData?.lastname,
        phone: contact?.phone ?? state.formData?.phone,
        email: contact?.email ?? state.formData?.email,
        address: contact?.address ?? state.formData?.address,
        zipCode: contact?.zipCode ?? state.formData?.zipCode,
        city: contact?.city ?? state.formData?.city,
        coAddress: contact?.coAddress ?? state.formData?.coAddress,
        otherInfo: latestIncident.otherInformation ?? state.formData?.otherInfo,
        signingMethod:
          latestIncident.signingType ?? state.formData?.signingMethod,
        latestEmpFormOfEmployment:
          latestIncident.formOfEmployment ??
          state.formData?.latestEmpFormOfEmployment,

        ...(latestEmployment
          ? {
              latestEmployment: latestEmployment.id,
              latestWorksiteId:
                latestEmployment.worksite?.id ??
                state.formData?.latestWorksiteId,
              latestEmpWorksitename:
                latestEmployment.worksite?.name ??
                state.formData?.latestEmpWorksitename,
              latestEmpWorksiteAddress:
                latestEmployment.worksite?.address ??
                state.formData?.latestEmpWorksiteAddress,
              latestEmpWorksiteAddressCountry:
                latestEmployment.worksite?.country ??
                state.formData?.latestEmpWorksiteAddressCountry,
              latestEmpWorksiteZipCode:
                latestEmployment.worksite?.zipCode ??
                state.formData?.latestEmpWorksiteZipCode,
              latestEmpWorksiteCity:
                latestEmployment.worksite?.city ??
                state.formData?.latestEmpWorksiteCity,
              latestEmpContactFirstname:
                latestEmployment.worksiteContactDetails?.firstname ??
                state.formData?.latestEmpContactFirstname,
              latestEmpContactLastname:
                latestEmployment.worksiteContactDetails?.lastname ??
                state.formData?.latestEmpContactLastname,
              latestEmpContactPhone:
                latestEmployment.worksiteContactDetails?.phone ??
                state.formData?.latestEmpContactPhone,
              latestEmpContactEmail:
                latestEmployment.worksiteContactDetails?.email ??
                state.formData?.latestEmpContactEmail,
              latestEmpStartDate:
                normalizeIsoDate(latestEmployment.startDate) ||
                state.formData?.latestEmpStartDate,
              latestEmpNoticeDate:
                normalizeIsoDate(latestEmployment.noticeDate) ||
                state.formData?.latestEmpNoticeDate,
              latestEmpEndDate:
                normalizeIsoDate(latestEmployment.endDate) ||
                state.formData?.latestEmpEndDate,
              latestEmpHasWorked16hConfirmation:
                latestEmployment.hasWorked16hConfirmation === true
                  ? ConfirmEmploymentEnum.Yes
                  : latestEmployment.hasWorked16hConfirmation === false
                    ? ConfirmEmploymentEnum.No
                    : state.formData?.latestEmpHasWorked16hConfirmation,
              latestEmpWorked16hComment:
                latestEmployment.comment ??
                state.formData?.latestEmpWorked16hComment,
            }
          : null),
      };
      const monthData = buildMonthData(
        state.employments,
        state.nonQualifyingPeriods,
        undefined,
        state.premiumPaymentThreshold
      );
      state.monthData = monthData;
    },
  },
});

export const {
  setPremiumPaymentThreshold,
  resetState,
  setActiveStep,
  setSessionId,
  setIsCompletingExistingApplication,
  setIsForaAssured,
  setApplicationType,
  setStartTexts,
  setFullTexts,
  setFormTexts,
  setResultData,
  setPersonData,
  setMonthData,
  addEmployment,
  removeEmployment,
  updateEmployment,
  updateEmployerAttachment,
  removeEmployerAttachment,
  addNonQualifyingPeriod,
  removeNonQualifyingPeriod,
  updateNonQualifyingPeriod,
  setLatestEmployment,
  createOrUpdateLatestEmployment,
  addSicknessAttachmentLatestEmployment,
  removeSicknessAttachmentLatestEmployment,
  addEmploymentCertificateLatestEmployment,
  removeEmploymentCertificateLatestEmployment,
  addWorkedTimeEmploymentCertificateLatestEmployment,
  removeWorkedTimeEmploymentCertificateLatestEmployment,
  addLatestPayslipAttachmentLatestEmployment,
  removeLatestPayslipAttachmentLatestEmployment,
  removeLatestEmployment,
  setFormData,
  setSignedApplicationDocument,
  prefillFromLatestIncident,
} = applicationSlice.actions;

export default applicationSlice.reducer;
